/**
 * @file apiResponseToast.ts
 * @module utils/apiResponseToast
 * @description Utilities for mapping backend response-message status payloads
 * to UI toasts in the admin app. Supports standardized response items with
 * message, status, and record fields.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.9
 * @version 2.1.2
 */
import { error, info, success, warning } from "../Global/toast";

/**
 * Supported status values from backend response messages.
 * `warn` is accepted as an alias of `warning` for older payloads.
 */
export type ApiResponseToastStatus = "info" | "success" | "warning" | "error";

/**
 * Shape of a single backend response-message item used for toast mapping.
 */
export type ApiResponseToastItem = {
  /** Human-readable toast message. */
  message?: string;
  /** Status that determines the toast style. */
  status?: ApiResponseToastStatus | "warn";
  /** Optional payload attached by backend, preserved for callers if needed. */
  record?: any[];
  /** Optional HTTP status code from ResponseMessage. */
  code?: number;
};

export type ApiResponseNotificationHandler = (
  status: ApiResponseToastStatus,
  message: string,
) => void;

/**
 * Whitelist of valid statuses accepted for toast mapping.
 */
const VALID_TOAST_STATUSES: ApiResponseToastStatus[] = [
  "info",
  "success",
  "warning",
  "error",
];

const normalizeStatus = (
  status: ApiResponseToastItem["status"],
): ApiResponseToastStatus => {
  if (status === "warn") return "warning";
  if (status && VALID_TOAST_STATUSES.includes(status as ApiResponseToastStatus)) {
    return status as ApiResponseToastStatus;
  }
  return "info";
};

/**
 * Resolves the final toast message for a response item.
 *
 * @param item - A backend response-message item.
 * @returns A non-empty toast message.
 */
const getToastMessage = (item: ApiResponseToastItem): string => {
  if (item?.message && item.message.trim()) {
    return item.message;
  }
  return "Operation completed.";
};

/**
 * Dispatches a toast based on a validated status.
 *
 * @param status - Toast status variant.
 * @param message - Toast text to display.
 */
const showToastByStatus = (status: ApiResponseToastStatus, message: string) => {
  if (status === "success") {
    success(message);
    return;
  }
  if (status === "warning") {
    warning(message);
    return;
  }
  if (status === "error") {
    error(message);
    return;
  }
  info(message);
};

/**
 * Converts backend response-message payloads to user-facing toasts.
 *
 * @param response - Single item or list returned by backend response-message format.
 * @returns void
 */
export const showToasterFromApiResponse = (
  response: ApiResponseToastItem[] | ApiResponseToastItem | null | undefined,
  notify: ApiResponseNotificationHandler = showToastByStatus,
) => {
  /** Normalized list of response items for uniform processing. */
  const items: ApiResponseToastItem[] = Array.isArray(response)
    ? response
    : response
      ? [response]
      : [];

  items.forEach((item) => {
    /** Final validated status used for toast routing. */
    const status = normalizeStatus(item?.status);
    notify(status, getToastMessage(item));
  });
};
